<script lang="ts">
import { defineComponent as __MACROS_defineComponent } from "vue";
export default /*#__PURE__*/ __MACROS_defineComponent({
  name: 'HorizontalNavGroup',
});
</script>
<script lang="ts" setup>
import { useLayouts } from '@layouts'
import { HorizontalNavLink, HorizontalNavPopper } from '@layouts/components'
import { config } from '@layouts/config'
import { canViewNavMenuGroup } from '@layouts/plugins/casl'
import type { NavGroup } from '@layouts/types'
import { isNavGroupActive } from '@layouts/utils'

interface Props {
  item: NavGroup
  childrenAtEnd?: boolean

  // ℹ️ We haven't added this prop in vertical nav because we don't need such differentiation in vertical nav for styling
  isSubItem?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  childrenAtEnd: false,
  isSubItem: false,
})



const route = useRoute()
const router = useRouter()
const { dynamicI18nProps, isAppRtl } = useLayouts()

const isGroupActive = ref(false)

/*
  Watch for route changes, more specifically route path. Do note that this won't trigger if route's query is updated.

  updates isActive & isOpen based on active state of group.
*/
watch(() => route.path, () => {
  const isActive = isNavGroupActive(props.item.children, router)

  isGroupActive.value = isActive
}, { immediate: true })
</script>

<template>
  <HorizontalNavPopper v-if="canViewNavMenuGroup(item)" :is-rtl="isAppRtl" class="nav-group" tag="li"
    content-container-tag="ul" :class="[{
      'active': isGroupActive,
      'children-at-end': childrenAtEnd,
      'sub-item': isSubItem,
      'disabled': item.disable,
    }]" :popper-inline-end="childrenAtEnd">
    <div class="nav-group-label">
      <Component :is="config.app.iconRenderer || 'div'" class="nav-item-icon"
        v-bind="item.icon || config.verticalNav.defaultNavItemIconProps" />
      <Component :is="config.app.enableI18n ? 'i18n-t' : 'span'" v-bind="dynamicI18nProps(item.title, 'span')"
        class="nav-item-title">
        {{ item.title }}
      </Component>
      <Component v-bind="config.icons.chevronDown" :is="config.app.iconRenderer || 'div'" class="nav-group-arrow" />
    </div>

    <template #content>
      <Component :is="'children' in child ? 'HorizontalNavGroup' : HorizontalNavLink" v-for="child in item.children"
        :key="child.title" :item="child" children-at-end is-sub-item />
    </template>
  </HorizontalNavPopper>
</template>

<style lang="scss">
.layout-horizontal-nav {
  .nav-group {
    .nav-group-label {
      display: flex;
      align-items: center;
      cursor: pointer;
    }

    .popper-content {
      z-index: 1;

      >div {
        overflow-x: hidden;
        overflow-y: auto;
      }
    }
  }
}

// Apply horizontal nav link styles to vertical nav links so vertical
// nav-links match the horizontal appearance. Kept local to layout
// styles (no changes to @core files as requested).

.layout-vertical-nav {

  // icon size like horizontal nav (also target svg inside anchor)
  .nav-item-icon,
  .nav-link>a .nav-item-icon,
  .nav-link .nav-item-icon {
    font-size: 1.375rem;
    margin-inline-end: 0.75rem;
  }

  // item padding like horizontal nav (also apply to anchor)
  .nav-link,
  .nav-link>a,
  .nav-group {
    padding-block: 0.6rem;
    border-radius: 0.4rem;
    position: relative;
  }

  // emulate before-pseudo mixin used by horizontal nav: hover/focus overlay
  .nav-link>a::before,
  .nav-link a::before {
    position: absolute;
    border-radius: inherit;
    background: currentcolor;
    block-size: 100%;
    content: "";
    inline-size: 100%;
    inset: 0;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.12s ease-in-out;
  }

  .nav-link>a:hover::before,
  .nav-link a:hover::before {
    opacity: 0.06;
  }

  .nav-link>a:active::before,
  .nav-link a:active::before,
  .nav-link>a:focus::before,
  .nav-link a:focus::before {
    opacity: 0.12;
  }

  // Top-level active nav link style (matching %nav-link-active)
  .nav-link:not(.sub-item)>a.router-link-active {
    background: linear-gradient(72.47deg, rgb(var(--v-global-theme-primary)) 22.16%, rgba(var(--v-global-theme-primary), 0.7) 76.47%) !important;
    box-shadow: 0 2px 6px rgba(var(--v-global-theme-primary), 0.48);
    font-weight: 500;
  }

  // Sub-item active style
  .nav-link.sub-item>a.router-link-active {
    background: rgba(var(--v-theme-primary), 0.08);
    color: inherit;

    &::before {
      content: none;
    }
  }

  // ensure nav-group label active state matches horizontal
  .nav-group.active>.nav-group-label {
    color: rgba(var(--v-theme-on-background), var(--v-high-emphasis-opacity));
    font-weight: 500;
  }
}
</style>
